/* eslint-disable max-len */
import {
    Box, FormControl, FormErrorMessage, FormHelperText, FormLabel, Text,
} from '@chakra-ui/react';
import { Controller, useFormContext } from 'react-hook-form';
import { BasicSelect } from '@acp/core-ui';
import { NotificationPreferenceType } from '@acp/shared';
import { useMemo } from 'react';
import { NotificationPreferenceFormValues } from './form-schema';
import { useNotificationPreferences } from '../../../agency';

export function AutoSendServiceReportsPreference() {
    const { control, watch } = useFormContext<NotificationPreferenceFormValues>();
    const { data, isLoading } = useNotificationPreferences();

    const serviceReportsAutoOwnerEmailPreferenceValue = watch('serviceReportsAutoOwnerEmailPreference');

    const serviceAutoOwnerEmailOptions = useMemo(() => {
        if (data === undefined || isLoading) return [];

        // Order the options as following
        const orderMap = {
            [NotificationPreferenceType.NotAllowed]: 0,
            [NotificationPreferenceType.OnlyCompliantServiceReports]: 1,
            [NotificationPreferenceType.AllServiceReports]: 2,
        };

        return data.serviceAutoOwnerEmailOptions.map((option) => ({
            label: option.name,
            value: option.id,
        })).sort((a, b) => orderMap[a.value] - orderMap[b.value]);
    }, [data, isLoading]);

    const serviceAutoOwnerEmailDescription = useMemo(() => {
        if (data === undefined || isLoading) return null;

        return data.serviceAutoOwnerEmailOptions
            .find((option) => option.id === serviceReportsAutoOwnerEmailPreferenceValue)?.description ?? null;
    }, [data, isLoading, serviceReportsAutoOwnerEmailPreferenceValue]);

    return (
        <Controller
            name="serviceReportsAutoOwnerEmailPreference"
            control={control}
            render={({ field, fieldState }) => (
                <FormControl isInvalid={fieldState.invalid}>
                    <FormLabel htmlFor="none" fontWeight="bold">
                        Automatically send service reports to owners
                    </FormLabel>
                    <FormHelperText color="muted.500" w={['100%', null, '70%']}>
                        <Text mb={4} lineHeight={1.3}>
                            This feature allows the selected property manager to automatically
                            send service reports to property owners. This ensures that owners are
                            promptly informed about service activities and updates, improving communication and efficiency.
                        </Text>
                        <Text>
                            Select a preference
                        </Text>
                    </FormHelperText>

                    <Box w="fit-content" mt={4} minW={['70%', null, '50%']}>
                        <BasicSelect
                            options={serviceAutoOwnerEmailOptions}
                            onChange={(option) => field.onChange(
                                option !== null ? option.value : null
                            )}
                            value={serviceReportsAutoOwnerEmailPreferenceValue ?? null}
                            placeholder="Select an option"
                            isClearable={false}
                        />
                    </Box>

                    <FormHelperText color="muted.500" w={['70%', null, '50%']}>
                        {serviceAutoOwnerEmailDescription}
                    </FormHelperText>

                    <FormErrorMessage>
                        {fieldState.error?.message}
                    </FormErrorMessage>
                </FormControl>
            )}
        />
    );
}
